/**
 * Footer — minimal global footer.
 *
 * Contains a company information section and two columns
 * of navigation links. No social media, newsletter, or
 * marketing content.
 */

import { Link } from 'react-router-dom';
import { Container } from '@/components/ui';
import { FOOTER_COLUMNS, FOOTER_COMPANY } from '@/config/navigation';
import { cn } from '@/lib/cn';
import { BrandLogo } from './BrandLogo';

export function Footer(): JSX.Element {
  return (
    <footer className="border-t border-border bg-surface">
      <Container maxWidth="2xl" className="py-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:justify-between">
          {/* Company information section */}
          <div className="max-w-sm">
            <Link
              to="/"
              className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="UNIVIBE home"
            >
              <BrandLogo className="h-8 shrink-0" />
            </Link>
            <p className="mt-4 text-sm leading-body text-muted-foreground">
              {FOOTER_COMPANY.description}
            </p>
          </div>

          {/* Link columns */}
          <div className="flex gap-16">
            {FOOTER_COLUMNS.map((column) => (
              <div key={column.title} className="flex flex-col gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  {column.title}
                </h3>
                <div className="flex flex-col gap-3">
                  {column.links.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={cn(
                        'text-sm text-muted-foreground tracking-tight',
                        'transition-colors duration-fast ease-standard hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:rounded',
                      )}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 border-t border-border pt-8">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} {FOOTER_COMPANY.name}. All rights reserved.
          </p>
        </div>
      </Container>
    </footer>
  );
}
